import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Database, KeyRound, Link2, Loader2, Plus, RefreshCw, ShieldCheck, Trash2, Unplug } from 'lucide-react';
import {
  connectSocialProvider,
  disconnectSocialProvider,
  getSocialAnalyticsConfig,
  listSocialConnections,
  syncSocialAnalytics
} from './lib/socialAnalyticsApi';
import { REEL_CREATOR_DEMO_MODE } from './lib/reelCreatorConfig';
import SocialApiPlaybook from './SocialApiPlaybook';

const providerOrder = ['instagram', 'reddit', 'tiktok', 'x', 'youtube'];
const fallbackProviders = {
  instagram: { label: 'Instagram', credentialFormEnabled: true, accountIdLabel: 'Instagram professional account ID', handleLabel: 'Instagram handle', tokenLabel: 'Meta access token', limitation: 'Instagram professional analytics through the official Meta API.', capabilities: { analytics: true, publishing: false } },
  tiktok: { label: 'TikTok', credentialFormEnabled: false, limitation: 'OAuth app setup and TikTok product approval are required.', capabilities: { analytics: true, publishing: false } },
  x: { label: 'X', credentialFormEnabled: true, accountIdLabel: 'X user ID', handleLabel: 'X handle', tokenLabel: 'X OAuth access token', limitation: 'Public post metrics are available; private analytics depend on the selected X API tier.', capabilities: { analytics: true, publishing: false } },
  reddit: { label: 'Reddit', credentialFormEnabled: true, accountIdLabel: 'Reddit username', handleLabel: 'Reddit username', tokenLabel: 'Reddit OAuth access token', limitation: 'Post monitoring is supported; Reddit does not expose creator post impressions.', capabilities: { analytics: true, publishing: false } },
  youtube: { label: 'YouTube / Shorts', credentialFormEnabled: false, limitation: 'Google OAuth and YouTube API consent are required.', capabilities: { analytics: true, publishing: false } }
};

const emptyDraft = (platform = 'instagram') => ({
  platform,
  accountId: '',
  accountHandle: '',
  accessToken: '',
  apiVersion: 'v23.0'
});

const formatDate = (value) => value
  ? new Date(value).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
  : 'Never synced';

function ConnectionBadge({ status, count = 0 }) {
  const connected = status === 'connected';
  const error = status === 'error';
  const className = connected
    ? 'border-green-500/30 bg-green-500/10 text-green-200'
    : error
      ? 'border-red-500/30 bg-red-500/10 text-red-200'
      : 'border-gray-700 bg-gray-800 text-gray-400';
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${className}`}>
      {connected ? <CheckCircle2 size={13} /> : error ? <AlertTriangle size={13} /> : <Unplug size={13} />}
      {status === 'not_connected' ? 'Not connected' : connected && count > 1 ? `${count} connected` : status}
    </span>
  );
}

function SocialConnections({ session, profile, dashboardId = 'vasia', onDataUpdated }) {
  const accessToken = session?.access_token;
  const canManage = ['admin', 'internal', 'model'].includes(profile?.role);
  const [config, setConfig] = useState(null);
  const [connections, setConnections] = useState([]);
  const [draft, setDraft] = useState(emptyDraft());
  const [editing, setEditing] = useState('');
  const [working, setWorking] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const providers = config?.providers || fallbackProviders;
  const selectedDefinition = providers[draft.platform] || {};
  const connectedCount = useMemo(
    () => connections.filter((connection) => connection.status === 'connected').length,
    [connections]
  );
  const savedConnectionCount = connections.length;

  const load = async () => {
    if (!accessToken) {
      setConfig({ configured: false, databaseConfigured: false, providers: fallbackProviders, reelCreator: { n8n: false, aiAnalysis: false, editingProvider: false, runway: false, storage: false } });
      setConnections([]);
      return;
    }
    setWorking('load');
    setError('');
    try {
      const [nextConfig, nextConnections] = await Promise.all([
        getSocialAnalyticsConfig({ accessToken, dashboardId }),
        listSocialConnections({ accessToken, dashboardId })
      ]);
      setConfig(nextConfig);
      setConnections(nextConnections.connections || []);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setWorking('');
    }
  };

  useEffect(() => { load(); }, [accessToken, dashboardId]);

  const startConnect = (platform) => {
    setDraft(emptyDraft(platform));
    setEditing(platform);
    setStatus('');
    setError('');
    requestAnimationFrame(() => document.getElementById('social-connection-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  };

  const submitConnection = async (event) => {
    event.preventDefault();
    setWorking(`connect:${draft.platform}`);
    setError('');
    setStatus('');
    try {
      await connectSocialProvider({ accessToken, dashboardId, connection: draft });
      setDraft(emptyDraft(draft.platform));
      setEditing('');
      setStatus(`${providers[draft.platform]?.label || draft.platform} credentials were encrypted and saved. Run a sync to populate the charts.`);
      await load();
    } catch (connectError) {
      setError(connectError.message);
    } finally {
      setWorking('');
    }
  };

  const sync = async (platform = '', connectionId = '') => {
    setWorking(connectionId ? `sync:${connectionId}` : platform ? `sync:${platform}` : 'sync:all');
    setError('');
    setStatus('');
    try {
      const payload = await syncSocialAnalytics({ accessToken, dashboardId, platform, connectionId });
      const failures = (payload.results || []).filter((result) => result.status === 'error');
      const syncedRows = (payload.results || []).reduce((sum, result) => sum + (result.rows || 0), 0);
      setStatus(failures.length
        ? `${syncedRows} weekly chart rows updated. ${failures.map((item) => `${item.platform}: ${item.error}`).join(' | ')}`
        : `${syncedRows} weekly chart rows updated from official read-only APIs.`);
      await load();
      if (onDataUpdated) await onDataUpdated();
    } catch (syncError) {
      setError(syncError.message);
    } finally {
      setWorking('');
    }
  };

  const disconnect = async (connection) => {
    const platform = connection.platform;
    const accountLabel = connection.accountHandle || connection.accountId || 'this account';
    if (!window.confirm(`Remove ${accountLabel} from ${providers[platform]?.label || platform}? Existing chart rows will stay in the database.`)) return;
    setWorking(`disconnect:${connection.connectionId}`);
    setError('');
    try {
      await disconnectSocialProvider({ accessToken, dashboardId, platform, connectionId: connection.connectionId });
      setStatus(`${accountLabel} was removed from ${providers[platform]?.label || platform}. Its stored API credentials were deleted.`);
      await load();
    } catch (disconnectError) {
      setError(disconnectError.message);
    } finally {
      setWorking('');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-wider text-gray-500">Secure connection hub</p>
          <h2 className="mt-1 text-2xl font-serif text-white">Social Media Connections</h2>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-400">
            Add multiple read-only API connections per platform, sync each account separately, and remove an individual key whenever needed. Publishing stays off.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={Boolean(working) || !accessToken} className="inline-flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm hover:bg-gray-700 disabled:opacity-50">
            <RefreshCw size={15} /> Refresh status
          </button>
          {canManage && <button onClick={() => sync()} disabled={Boolean(working) || savedConnectionCount === 0 || !accessToken} className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-500">
            {working === 'sync:all' ? <Loader2 size={15} className="animate-spin" /> : <Database size={15} />} Sync all charts
          </button>}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-gray-800 bg-gray-900 p-5">
          <ShieldCheck className="text-green-300" size={20} />
          <p className="mt-3 text-sm text-gray-400">Credential storage</p>
          <p className="mt-1 font-semibold text-white">{config?.configured ? 'Encrypted vault ready' : 'Setup required'}</p>
        </div>
        <div className="rounded-xl border border-gray-800 bg-gray-900 p-5">
          <Link2 className="text-purple-300" size={20} />
          <p className="mt-3 text-sm text-gray-400">Connected accounts</p>
          <p className="mt-1 font-semibold text-white">{connectedCount} active · {savedConnectionCount} saved</p>
        </div>
        <div className="rounded-xl border border-gray-800 bg-gray-900 p-5">
          <Database className="text-blue-300" size={20} />
          <p className="mt-3 text-sm text-gray-400">Chart database</p>
          <p className="mt-1 font-semibold text-white">{config?.databaseConfigured ? 'Supabase ready' : 'Setup required'}</p>
        </div>
      </div>

      {!config?.configured && config && canManage && accessToken && (
        <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-4 text-sm leading-6 text-yellow-100">
          The secure Cloudflare credential vault still needs its KV binding and encryption secret. Connection forms remain blocked until that production setup is complete.
        </div>
      )}
      {status && <div className="rounded-xl border border-green-500/30 bg-green-500/10 p-4 text-sm text-green-100">{status}</div>}
      {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-100">{error}</div>}

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
        {providerOrder.map((platform) => {
          const definition = providers[platform] || {};
          const platformConnections = connections.filter((item) => item.platform === platform);
          const connectedForPlatform = platformConnections.filter((item) => item.status === 'connected').length;
          const platformStatus = connectedForPlatform ? 'connected' : platformConnections.some((item) => item.status === 'error') ? 'error' : 'not_connected';
          return (
            <div key={platform} className="rounded-xl border border-gray-800 bg-gray-900 p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-white">{definition.label || platform}</h3>
                  <p className="mt-1 text-xs text-gray-500">Analytics available · Publishing off</p>
                </div>
                <ConnectionBadge status={platformStatus} count={connectedForPlatform} />
              </div>
              <p className="mt-4 min-h-[72px] text-sm leading-6 text-gray-400">{definition.limitation}</p>
              {platformConnections.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Saved API connections</p>
                  {platformConnections.map((connection) => {
                    const isSyncing = working === `sync:${connection.connectionId}`;
                    const isRemoving = working === `disconnect:${connection.connectionId}`;
                    return (
                      <div key={connection.connectionId} className="rounded-lg border border-gray-800 bg-gray-950/60 p-3 text-xs text-gray-400">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-gray-200">{connection.accountHandle || connection.accountId}</p>
                            <p className="mt-1 truncate">ID: {connection.accountId}</p>
                            <p className="mt-1">Last sync: {formatDate(connection.lastSyncedAt)}</p>
                          </div>
                          <ConnectionBadge status={connection.status} />
                        </div>
                        {connection.lastError && <p className="mt-2 text-red-300">{connection.lastError}</p>}
                        {canManage && (
                          <div className="mt-3 flex gap-2">
                            <button onClick={() => sync(platform, connection.connectionId)} disabled={Boolean(working)} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-700 px-2.5 py-2 text-xs text-gray-200 hover:bg-gray-800 disabled:opacity-50">
                              {isSyncing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Sync
                            </button>
                            <button onClick={() => disconnect(connection)} disabled={Boolean(working)} className="inline-flex items-center gap-1.5 rounded-lg border border-red-500/30 px-2.5 py-2 text-xs text-red-300 hover:bg-red-500/10 disabled:opacity-50">
                              {isRemoving ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Remove
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {canManage && <div className="mt-5 flex flex-wrap gap-2">
                {definition.credentialFormEnabled ? <button onClick={() => startConnect(platform)} disabled={!config?.configured || Boolean(working) || !accessToken} className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-3 py-2 text-sm hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-500">
                  <Plus size={15} /> Add API key
                </button> : <button disabled className="rounded-lg bg-gray-800 px-3 py-2 text-sm text-gray-500">OAuth setup required</button>}
              </div>}
            </div>
          );
        })}
      </div>

      {canManage && <SocialApiPlaybook />}

      {canManage && (
        <section className="rounded-xl border border-gray-800 bg-gray-900 p-5">
          <p className="text-xs uppercase tracking-wider text-gray-500">Internal status only</p>
          <h3 className="mt-1 text-lg font-semibold text-white">AI Reel Creator Connections</h3>
          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              ['AI Analysis', config?.reelCreator?.aiAnalysis ? 'Configured' : REEL_CREATOR_DEMO_MODE ? 'Demo Mode' : 'Not Configured'],
              ['n8n', REEL_CREATOR_DEMO_MODE ? 'Demo Mode' : config?.reelCreator?.n8n ? 'Connected' : 'Not Connected'],
              ['Editing Provider', config?.reelCreator?.editingProvider ? 'Connected' : REEL_CREATOR_DEMO_MODE ? 'Demo Mode' : 'Not Connected'],
              ['Runway', config?.reelCreator?.runway ? 'Connected' : 'Optional · Not Connected'],
              ['Storage', config?.reelCreator?.storage ? 'Connected' : REEL_CREATOR_DEMO_MODE ? 'Demo Storage' : 'Not Connected']
            ].map(([label, value]) => <div key={label} className="rounded-lg border border-gray-800 bg-gray-950/60 p-4"><p className="text-xs text-gray-500">{label}</p><p className={`mt-1 font-semibold ${/Connected|Configured|Demo/.test(value) ? 'text-green-300' : 'text-gray-300'}`}>{value}</p></div>)}
          </div>
          <p className="mt-4 text-xs leading-5 text-gray-500">Secrets stay in Cloudflare environment settings and the encrypted credential vault. They are never displayed here.</p>
        </section>
      )}

      {editing && canManage && (
        <form id="social-connection-form" onSubmit={submitConnection} className="rounded-xl border border-purple-500/30 bg-gray-900 p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-purple-300"><KeyRound size={14} /> Add {selectedDefinition.label || editing} API key</p>
              <h3 className="mt-1 text-lg font-semibold text-white">Connect another account</h3>
              <p className="mt-2 text-sm text-gray-400">Each account is stored separately, so you can connect multiple accounts for the same platform.</p>
            </div>
            <button type="button" onClick={() => setEditing('')} className="text-sm text-gray-400 hover:text-white">Cancel</button>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="text-xs text-gray-400">
              {selectedDefinition.accountIdLabel || 'Account ID'}
              <input required value={draft.accountId} onChange={(event) => setDraft({ ...draft, accountId: event.target.value })} className="mt-1 w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white focus:border-purple-500 focus:outline-none" />
            </label>
            <label className="text-xs text-gray-400">
              {selectedDefinition.handleLabel || 'Account handle'}
              <input required value={draft.accountHandle} onChange={(event) => setDraft({ ...draft, accountHandle: event.target.value })} placeholder="@vasia" className="mt-1 w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white focus:border-purple-500 focus:outline-none" />
            </label>
            {draft.platform === 'instagram' && (
              <label className="text-xs text-gray-400">
                Meta Graph API version
                <input required value={draft.apiVersion} onChange={(event) => setDraft({ ...draft, apiVersion: event.target.value })} className="mt-1 w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white focus:border-purple-500 focus:outline-none" />
              </label>
            )}
            <label className="text-xs text-gray-400 md:col-span-2">
              {selectedDefinition.tokenLabel || 'Read-only access token'}
              <input required type="password" autoComplete="off" value={draft.accessToken} onChange={(event) => setDraft({ ...draft, accessToken: event.target.value })} className="mt-1 w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white focus:border-purple-500 focus:outline-none" />
              <span className="mt-2 block text-xs leading-5 text-gray-500">This value is cleared from the form after saving. Do not send tokens in Telegram, email, screenshots or GitHub.</span>
            </label>
          </div>
          <button type="submit" disabled={Boolean(working)} className="mt-5 inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium hover:bg-purple-500 disabled:bg-gray-700">
            {working.startsWith('connect:') && <Loader2 size={15} className="animate-spin" />} Encrypt and save connection
          </button>
        </form>
      )}
    </div>
  );
}

export default SocialConnections;
